import { clsx } from '@alexis/helpers/clsx';
import { locales } from '@alexis/helpers/site';
import { debounce } from 'lodash';
import { capitalize } from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/Button';

import { REVIEW_KEY_PHRASE } from '@constants/hotels';

import { notUndefinedAndNull } from '@helpers/array';

import ChevronDown from '@icons/chevron_down_small.svg';
import CloseFilledIcon from '@icons/circle_filled_cross.svg';
import SearchIcon from '@icons/search.svg';

import useClickOutside from '@hooks/useClickOutside';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { REVIEW_GUEST_TYPE, REVIEW_SORT_BY } from '@wegoTypes/hotels/hotelGuestReview';

import styles from './FilterReviews.module.scss';

export interface ReviewsFilters {
  sortBy: Array<`${REVIEW_SORT_BY}`>;
  languageSelector: string;
  guestType: REVIEW_GUEST_TYPE;
  queries?: string;
  keyPhrase?: typeof REVIEW_KEY_PHRASE;
}

interface FilterReviewsProps {
  filters: ReviewsFilters;
  setFilters: (filters: ReviewsFilters) => void;
  setCurrentPage: (page: number) => void;
}

const FilterReviews: React.FC<FilterReviewsProps> = ({ filters, setFilters, setCurrentPage }) => {
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);

  const searchboxRef = useRef<HTMLInputElement | null>(null);

  const [sortType, setSortType] = useState('');
  const [keyphraseExpanded, setKeyphraseExpanded] = useState(false);
  const [isOpenSearchbox, setIsOpenSearchbox] = useState(false);

  const [selectedSortBy, setSelectedSortBy] = useState('most_recent');
  const [selectedLanguage, setSelectedLanguage] = useState(filters.languageSelector);
  const [selectedGuestType, setSelectedGuestType] = useState('');
  const [selectedKeyphrase, setSelectedKeyphrase] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const displayedLocales = [
    ...locales.filter((item) => ['en', 'ar', 'ms'].includes(item.code)), // English and Arabic (fixed)
    locales.find((item) => item.code === locale && !['en', 'ar', 'ms'].includes(item.code)), // User locale (if not a default)
    { code: '', name: translations.all },
  ].filter((item) => notUndefinedAndNull(item));

  const displayedKeyPhrases = keyphraseExpanded ? REVIEW_KEY_PHRASE : REVIEW_KEY_PHRASE.slice(0, 4);

  const clickOutsideRef = useClickOutside(() => {
    if (sortType) setSortType('');
  });

  const handleSelectKeyphrase = (keyphrase: string) => {
    let listKeyPhrase = [];

    if (selectedKeyphrase.includes(keyphrase)) {
      listKeyPhrase = selectedKeyphrase.filter((phrase) => phrase !== keyphrase);
      setSelectedKeyphrase(selectedKeyphrase.filter((phrase) => phrase !== keyphrase));
    } else {
      listKeyPhrase = [...selectedKeyphrase, keyphrase];
      setSelectedKeyphrase([...selectedKeyphrase, keyphrase]);
    }
    setFilters({ ...filters, keyPhrase: listKeyPhrase });
  };

  const handleOnSearchChanged = useCallback(
    debounce((searchValue) => {
      setSearchValue(searchValue);
      setFilters({ ...filters, queries: searchValue });
    }, 300),
    [filters],
  );

  const handleOnClearSearch = () => {
    setSearchValue('');
    setFilters({ ...filters, queries: '' });
  };

  const handleLocaleChanged = (locale: (typeof displayedLocales)[number]) => {
    if (locale.code !== filters.languageSelector) {
      // reset page when changing language
      setCurrentPage(1);
    }
    setFilters({ ...filters, languageSelector: locale.code });
    setSelectedLanguage(locale.code);
  };

  useEffect(() => {
    if (isOpenSearchbox) {
      searchboxRef.current?.focus();
    }
  }, [isOpenSearchbox]);

  return (
    <div className={styles.container}>
      <strong>{translations.filters}</strong>

      <div className={styles.sortSection} ref={clickOutsideRef}>
        <div
          className={styles.sortItem}
          data-open={sortType === 'sortBy'}
          onClick={() => {
            setSortType(sortType === 'sortBy' ? '' : 'sortBy');
          }}
        >
          <div className={styles.dropdownHeader}>
            <div>
              <div className={styles.title}>{String(translations.sort_by).replace(/:/g, '')}</div>
              <div className={styles.selected}>{translations[selectedSortBy]}</div>
            </div>

            <ChevronDown className={styles.chevron} />
          </div>
          {sortType === 'sortBy' ? (
            <div className={styles.dropdownContent}>
              {Object.entries(REVIEW_SORT_BY).map(([key, value]) => {
                return (
                  <div
                    key={key}
                    className={clsx(styles.item, filters.sortBy.includes(value) && styles.selected)}
                    onClick={() => {
                      setFilters({ ...filters, sortBy: [value] });
                      setSelectedSortBy(key);
                    }}
                  >
                    {translations[key]}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div
          className={styles.sortItem}
          data-open={sortType === 'lang'}
          onClick={() => setSortType(sortType === 'lang' ? '' : 'lang')}
        >
          <div className={styles.dropdownHeader}>
            <div>
              <div className={styles.title}>{translations.language}</div>
              <div className={styles.selected}>
                {displayedLocales.find((locale) => locale.code === selectedLanguage)?.name}
              </div>
            </div>

            <ChevronDown className={styles.chevron} />
          </div>

          {sortType === 'lang' ? (
            <div className={styles.dropdownContent}>
              {displayedLocales.map((locale) => {
                return (
                  <div
                    key={locale.code}
                    className={clsx(
                      styles.item,
                      selectedLanguage === locale.code && styles.selected,
                    )}
                    onClick={() => handleLocaleChanged(locale)}
                  >
                    {locale.name}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div
          className={styles.sortItem}
          data-open={sortType === 'guestType'}
          onClick={() => setSortType(sortType === 'guestType' ? '' : 'guestType')}
        >
          <div className={styles.dropdownHeader}>
            <div>
              <div className={styles.title}>{translations.guest_type}</div>
              <div className={styles.selected}>
                {translations[selectedGuestType || 'all_guests']}
              </div>
            </div>

            <ChevronDown className={styles.chevron} />
          </div>

          {sortType === 'guestType' ? (
            <div className={styles.dropdownContent}>
              {Object.entries(REVIEW_GUEST_TYPE).map(([key, type]) => {
                return (
                  <div
                    key={key}
                    className={clsx(styles.item, selectedGuestType === type && styles.selected)}
                    onClick={() => {
                      setSelectedGuestType(key);
                      setFilters({ ...filters, guestType: type as REVIEW_GUEST_TYPE });
                    }}
                  >
                    {translations[key]}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.keyPhraseContainer}>
        {displayedKeyPhrases.map((keyphrase) => {
          return (
            <Button
              key={keyphrase}
              colorScheme='grey'
              pill
              size='md'
              variant='outline'
              className={clsx(
                styles.keyphrase,
                selectedKeyphrase.includes(keyphrase) && styles.selected,
              )}
              onClick={() => handleSelectKeyphrase(keyphrase)}
            >
              {capitalize(translations[keyphrase] as string)}
            </Button>
          );
        })}
        <Button
          key='more'
          colorScheme='grey'
          pill
          size='md'
          variant='outline'
          className={styles.keyphrase}
          onClick={() => setKeyphraseExpanded(!keyphraseExpanded)}
        >
          {!keyphraseExpanded ? `+ ${translations.show_more}` : `- ${translations.show_less}`}
        </Button>

        <Button
          key='icon'
          colorScheme='grey'
          pill
          size='md'
          variant='outline'
          className={styles.keyphrase}
          onClick={() => setIsOpenSearchbox(true)}
        >
          <SearchIcon className={styles.searchIcon} />
        </Button>
      </div>

      <div className={styles.searchContainer} data-open={isOpenSearchbox}>
        <div className={styles.searchBar}>
          <SearchIcon className={styles.searchIcon} />
          <div className={styles.searchTitle}>{translations.search}</div>
          <div className={styles.divider} />
          <input
            autoComplete='off'
            inputMode='text'
            onChange={(e) => {
              setSearchValue(e.target.value);
              handleOnSearchChanged(e.target.value);
            }}
            placeholder={translations.search_reviews as string}
            spellCheck={false}
            type='text'
            value={searchValue || ''}
            ref={searchboxRef}
          />
          {searchValue ? (
            <div
              className={styles.closeIcon}
              onClick={() => {
                searchboxRef.current?.focus();
                handleOnClearSearch();
              }}
            >
              <CloseFilledIcon />
            </div>
          ) : null}
        </div>

        <Button
          key='icon'
          colorScheme='grey'
          size='md'
          variant='ghost'
          className={styles.cancelButton}
          onClick={() => {
            setIsOpenSearchbox((prev) => !prev);
            handleOnClearSearch();
          }}
        >
          {translations.cancel}
        </Button>
      </div>
    </div>
  );
};

export default FilterReviews;
